import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, Star, BarChart2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { priceInfo } from "../mock";

export function formatPrice(n) {
  return "₺ " + Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 0 });
}

export default function ProductCard({ product }) {
  const { t, tr, lang, wishlist, toggleWishlist, compare, toggleCompare, trackProductClick, getBadges } = useApp();
  const [hover, setHover] = useState(false);
  const inWish = wishlist.includes(product.id);
  const inComp = compare.includes(product.id);
  const img2 = product.images[1] || product.images[0];
  const name = lang === "en" && product.nameEn ? product.nameEn : product.name;
  const badges = getBadges();
  const pi = priceInfo(product);

  const handleCompare = () => {
    if (!inComp && compare.length >= 4) return toast.error(t("maxCompare"));
    toggleCompare(product.id);
    if (!inComp) toast.success(t("addedCompare"));
  };

  return (
    <div className="group" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="relative overflow-hidden bg-neutral-100 aspect-[3/4]">
        {/* Badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          {pi.hasDiscount && (
            <span className="text-[9px] tracking-widest uppercase text-white px-2.5 py-1 font-semibold" style={{ backgroundColor: "#c1121f" }}>
              %{pi.pct} {t("discountOff")}
            </span>
          )}
          {(product.badges || []).map((b) => (
            <span key={b} className="text-[9px] tracking-widest uppercase text-white px-2.5 py-1 font-medium" style={{ backgroundColor: badges[b]?.color || "#800000" }}>
              {tr(badges[b]) || b}
            </span>
          ))}
        </div>

        <Link to={`/product/${product.id}`} onClick={() => trackProductClick(product.id)}>
          <img src={product.images[0]} alt={name} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hover ? "opacity-0" : "opacity-100"}`} />
          <img src={img2} alt={name} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hover ? "opacity-100" : "opacity-0"}`} />
        </Link>

        {/* Actions */}
        <div className={`absolute top-3 right-3 z-10 flex flex-col gap-2 transition-all duration-300 ${hover ? "opacity-100 translate-x-0" : "opacity-0 translate-x-3"}`}>
          <button onClick={() => toggleWishlist(product.id)} className="w-9 h-9 bg-white/95 hover:bg-white flex items-center justify-center shadow-sm transition-colors" aria-label="wishlist" title={t("addToWishlist")}>
            <Heart size={16} className={inWish ? "fill-current" : ""} style={{ color: inWish ? "var(--brand)" : "#404040" }} />
          </button>
          <button onClick={handleCompare} className="w-9 h-9 bg-white/95 hover:bg-white flex items-center justify-center shadow-sm transition-colors" aria-label="compare" title={t("addToCompare")}>
            <BarChart2 size={16} style={{ color: inComp ? "var(--brand)" : "#404040" }} />
          </button>
          <Link to={`/product/${product.id}`} onClick={() => trackProductClick(product.id)} className="w-9 h-9 bg-white/95 hover:bg-white flex items-center justify-center shadow-sm transition-colors" aria-label="view" title={t("quickView")}>
            <Eye size={16} className="text-neutral-700" />
          </Link>
        </div>
      </div>

      {/* Info */}
      <div className="pt-4 text-center">
        <div className="flex items-center justify-center gap-0.5 mb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={12} className={i <= Math.round(product.rating) ? "fill-current" : ""} style={{ color: i <= Math.round(product.rating) ? "var(--brand)" : "#d4d4d4" }} />
          ))}
        </div>
        <Link to={`/product/${product.id}`} onClick={() => trackProductClick(product.id)}>
          <h3 className="text-sm tracking-wide text-neutral-800 hover:text-[color:var(--brand)] transition-colors font-light px-2">{name}</h3>
        </Link>
        <div className="mt-2 flex items-center justify-center gap-2">
          {pi.hasDiscount ? (
            <>
              <span className="text-sm line-through" style={{ color: "#c1121f" }}>{formatPrice(pi.original)}</span>
              <span className="text-sm tracking-wide text-neutral-900 font-semibold">{formatPrice(pi.final)}</span>
            </>
          ) : (
            <span className="text-sm tracking-wide text-neutral-900 font-medium">{formatPrice(pi.final)}</span>
          )}
        </div>
      </div>
    </div>
  );
}
